import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  saveSuite,
  updateSuite,
  deleteUnit,
  setWalkThroughStatus,
  onClearResponseDone,
} from 'src/store/actions/suites';
import { updateSuiteSynchronization } from 'src/services/client';

import SuitesTable from '../../components/SuitesTable';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { clearSearch } from 'src/store/actions/search';
import { upload } from 'src/helpers/upLoadHelper';
import Loader from '../../components/Loader';
import { selectPRLoader } from 'src/store/selectors/PropertyLoader';
import moment from 'moment';
import { fetchPropertyCounts } from 'src/store/actions/properties';
import { clearFaqsNotesDocuments } from 'src/store/actions/faqsNotesDocuments';
import { getAllUserTypeRoles } from 'src/store/actions/formOptions';
import { clearValidation } from 'src/store/actions/validation';
import useFetchProperties from 'src/pages/SharedPageCustomHooks/UseFetchProperties/useFetchProperties';
import { toast } from 'react-toastify';

const PropertySuites = ({ property }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectAuthenticatedUser);
  const loader = useSelector(selectPRLoader);

  const { fetchPropertySuites, data, setData, loading } = useFetchProperties(
    {}
  );

  const onSaveSuite = (params: any) => dispatch(saveSuite(params));
  const onUpdateSuite = (params: any) => dispatch(updateSuite(params));
  const onDeleteUnit = (unitId: any, userId: any) =>
    dispatch(deleteUnit(unitId, userId));
  const onSetWalkThroughStatus = (status: any, data: any) =>
    dispatch(setWalkThroughStatus(status, data));

  const onClearResponse = () => dispatch(onClearResponseDone());
  const onClearValidation = () => dispatch(clearValidation());
  const onFetchPropertyCounts = (propertyId: any, userId: any, roleName: any) =>
    dispatch(fetchPropertyCounts(propertyId, userId, roleName));
  const onClearFaqsNotesDocuments = () => dispatch(clearFaqsNotesDocuments());
  const onGetAllUserTypeRoles = () => dispatch(getAllUserTypeRoles());
  const onClearSearch = () => dispatch(clearSearch());

  useEffect(() => {
    fetchPropertySuites(property.propertyId);
    onClearFaqsNotesDocuments();
    onClearSearch();
    onGetAllUserTypeRoles();
    clearSuitesImportResponse();
  }, []);

  const clearSuitesImportResponse = () => {
    onClearValidation();
    onClearResponse();
  };

  const handleSaveSuite = async (
    suite: any,
    suiteAreas: any,
    floorImage: any
  ) => {
    let imageUrl = null;
    let newImage = false;
    if (floorImage === suite.floorPlanURL) {
      imageUrl = suite.floorPlanURL;
    } else {
      if (suite.floorPlanURL && suite.floorPlanURL.length > 0) {
        newImage = true;
        imageUrl = await upload(suite.floorPlanURL[0]);
      }
    }

    let params = {};
    let parkingStalls = [];
    let storageLockers = [];
    if (suite && suite.parkingStalls && suite.parkingStalls.length > 0) {
      suite.parkingStalls.map((ps) => {
        let obj = {
          Name: ps.label,
          Type: 'ParkingStalls',
        };
        parkingStalls = [...parkingStalls, obj];
      });
    }
    if (suite && suite.storageLockers && suite.storageLockers.length > 0) {
      suite.storageLockers.map((ps) => {
        let obj = {
          Name: ps.label,
          Type: 'StorageLockers',
        };
        storageLockers = [...storageLockers, obj];
      });
    }
    if (suite.id) {
      await suiteAreas.map((item) => {
        if (item.type === 'Other') {
          delete item.id;
        }
      });
      params = {
        id: suite.id,
        propertyId: suite.propertyId,
        number: suite.number.trim(),
        stratalot: suite.stratalot ? suite.stratalot.trim() : '',
        Userid: user.id,
        Areas: suiteAreas,
        floorPlanURL: imageUrl,
        assetWarrantyDefaultStartDate: suite.assetWarrantyDefaultStartDate
          ? new Date(moment(suite.assetWarrantyDefaultStartDate).format('l LT'))
          : null,
        warrantyNumber: suite.warrantyNumber,
        warrantyAddress: suite.warrantyAddress,
        Building: suite.Building,
        BuildingId: suite.BuildingId ? suite.BuildingId : null,
        Level: suite.Level,
        LevelId: suite.LevelId ? suite.LevelId : null,
        ParkingStalls: parkingStalls,
        StorageLockers: storageLockers,
        newImage: newImage,
        KeyFobs: suite.keyFobs,
        NumberOfKeys:
          suite.numberOfKeys === null ? 0 : Number(suite.numberOfKeys),
        KeyHandOffInstructions: suite.keyHandOffInstructions,
        StatusId: suite.statusId,
        suiteKeysAndValues: suite.suiteKeysAndValues,
      };

      onUpdateSuite(params);
    } else {
      await suiteAreas.map((item) => {
        if (item.type === 'Other') {
          delete item.id;
        }
      });
      params = {
        fromUnitId: suite && suite.fromUnitId ? suite.fromUnitId : null,
        Number: suite.number.trim(),
        stratalot: suite.stratalot ? suite.stratalot.trim() : '',
        PropertyId: property.propertyId,
        Userid: user.id,
        Areas: suiteAreas,
        floorPlanURL: imageUrl,
        assetWarrantyDefaultStartDate: suite.assetWarrantyDefaultStartDate
          ? new Date(moment(suite.assetWarrantyDefaultStartDate).format('l LT'))
          : null,
        warrantyNumber: suite.warrantyNumber,
        warrantyAddress: suite.warrantyAddress,
        Building: suite.Building,
        BuildingId: suite.BuildingId ? suite.BuildingId : null,
        Level: suite.Level,
        LevelId: suite.LevelId ? suite.LevelId : null,
        ParkingStalls: parkingStalls,
        StorageLockers: storageLockers,
        KeyFobs: suite.keyFobs,
        NumberOfKeys:
          suite.numberOfKeys === null ||
          suite.numberOfKeys === '' ||
          suite.numberOfKeys === undefined
            ? 0
            : Number(suite.numberOfKeys),
        KeyHandOffInstructions: suite.keyHandOffInstructions,
        StatusId: suite.statusId,
        suiteKeysAndValues: suite.suiteKeysAndValues,
      };

      onSaveSuite(params);
      onFetchPropertyCounts(
        property.propertyId,
        user.id,
        user.roles[0].role.name
      );
    }
  };

  const handleDeleteUnit = async (unitId: any) => {
    onDeleteUnit(unitId, user.id);
    onFetchPropertyCounts(
      property.propertyId,
      user.id,
      user.roles[0].role.name
    );
  };

  const handleSetWalkThroughStatus = (status: any, data: any) => {
    onSetWalkThroughStatus(status, data);
  };

  const handleToggleMobileSync = (
    suiteId: string,
    mobileSyncAllowed: boolean
  ) => {
    updateSuiteSynchronization(suiteId, mobileSyncAllowed)
      .then((res) => {
        if (res instanceof Response && !res.ok) {
          toast.error('🚫 Mobile Sync update failed.', {
            autoClose: 10000,
            position: 'top-center',
          });
          return;
        }

        fetchPropertySuites(property.propertyId);
      })
      .catch((err) => {
        toast.error('🚫 Mobile Sync update failed.', {
          autoClose: 10000,
          position: 'top-center',
        });
      });
  };

  const isLoading: any = loader || loading.propertySuite;

  return isLoading ? (
    <Loader />
  ) : (
    <SuitesTable
      suites={data.suitesList}
      property={property}
      onDeleteUnit={(data: any) => handleDeleteUnit(data)}
      setWalkThroughStatus={(status: any, data: any) =>
        handleSetWalkThroughStatus(status, data)
      }
      onSaveSuite={(suite: any, areas: any, floorImage: any) =>
        handleSaveSuite(suite, areas, floorImage)
      }
      onToggleMobileSync={(suiteId, mobileSyncAllowed) =>
        handleToggleMobileSync(suiteId, mobileSyncAllowed)
      }
      fetchPropertySuites={() => fetchPropertySuites(property.propertyId)}
    />
  );
};

export default PropertySuites;
